import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { isNotNullish } from '@y42/util/nullish';
import isEqual from 'lodash/isEqual';
import { combineLatest, merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, startWith, withLatestFrom } from 'rxjs/operators';
import type { GitBranch, GitFSStatus, GitTreeItem } from '../worker';
import { GitEventType, GitEventTypeToGitEvent } from './git-event.model';
import { GitChangesConfig } from './git.model';
import { GitState, GitStore } from './git.store';

@Injectable({ providedIn: 'root' })
export class GitQuery extends QueryEntity<GitState> {
  constructor(protected readonly store: GitStore) {
    super(store);
  }

  readonly loading$ = this.select((state) => state.ui.loading > 0);
  readonly nameOfInitializedRepo$ = this.select((st) => st.ui.repoInitializedFor || null);
  readonly repoInitialized$ = this.nameOfInitializedRepo$.pipe(map(isNotNullish));

  readonly event$ = this.store.event$;
  readonly gitStatus$ = this.select((st) => st.ui.status);
  readonly gitStatusCode$ = this.select((st) => st.ui.status?.status || null);
  readonly gitOperationOngoing$ = this.isOperationOngoing({ exclude: ['read', 'write', 'checkout-conflict'] });
  readonly gitStatusDescription$ = this.select((st) => st.ui.status).pipe(
    map((gitStatus) => {
      if (!gitStatus) {
        return '';
      }

      const { params, status, error } = gitStatus;

      if (error) {
        return error.message;
      }

      const statusToLabel: Record<GitFSStatus['status'], string> = {
        init: 'Initializing file system',
        clone: 'Cloning repository',
        checkout: `Checking out branch${params ? ` "${params}"` : ''}`,
        fetch: 'Fetching repository status',
        pull: 'Pulling latest changes',
        reset: 'Discarding changes',
        revert: 'Reverting changes',
        commit: 'Committing changes',
        push: 'Pushing changes to origin',
        read: 'Reading from file system',
        write: 'Writing to file system',
        delete: 'Deleting from file system',
        'checkout-conflict': 'Checkout conflict',
        conflict: 'Conflict',
        merge: 'Merging into current branch',
        stash: 'Stashing changes',
      };
      return statusToLabel[status];
    }),
  );

  readonly activeBranch$ = this.selectActive().pipe(filter(Boolean));

  readonly activeBranchName$ = this.activeBranch$.pipe(
    map((branch) => branch.name),
    distinctUntilChanged(),
  );

  readonly isActiveBrachDefault$ = this.select((state) => state.default === state.active);

  readonly branches$ = this.selectAll();

  readonly defaultBranch$ = this.select((st) => st.default).pipe(
    filter(Boolean),
    withLatestFrom(this.select((st) => st.entities)),
    map(([defaultBranch, entities]) => entities?.[defaultBranch]),
    filter(Boolean),
  );

  readonly fileTree$ = this.activeBranch$.pipe(
    map((branch) => branch.tree),
    filter(Boolean),
  );

  readonly activeTreeItem$ = this.select((state) => state.ui.activeTreeItem);

  readonly fileStatuses$ = this.select((st) => st.ui.fileStatuses).pipe(distinctUntilChanged((a, b) => isEqual(a, b)));

  readonly modifiedFiles$ = this.fileStatuses$.pipe(map(() => this.getModifiedFiles()));
  readonly modifiedFilesList$ = this.modifiedFiles$.pipe(map(() => this.getModifiedFilesList()));
  readonly hasModifiedFiles$ = this.modifiedFilesList$.pipe(
    map((modifiedFiles) => modifiedFiles.length > 0),
    distinctUntilChanged(),
  );

  readonly numberOfConflictedFiles$ = this.select((st) => st.ui.numberOfConflictedFiles);

  readonly committerDate$ = this.selectActive((branch) => branch.committerDate);

  readonly aheadBehind$ = combineLatest([this.select((st) => st.ui.ahead), this.select((st) => st.ui.behind)]).pipe(
    map(([ahead, behind]) => ({ ahead, behind })),
  );

  readonly numberOfFilesOnBranch$ = this.activeBranch$.pipe(map((branch) => branch.numberOfFiles || 0));

  readonly trigger$ = merge(
    this.wasInStatus('pull').pipe(map(() => 'git pull')),
    this.wasInStatus('stash').pipe(map(() => 'git stash')),
    this.wasInStatus('reset').pipe(map(() => 'git reset')),
    this.wasInStatus('checkout').pipe(map(() => 'git checkout')),
    this.wasInStatus('merge').pipe(map(() => 'git merge')),
    this.wasInStatus('revert').pipe(map(() => 'git revert')),
    this.listen(GitEventType.ForceUpdateUI).pipe(map(() => 'refresh git UI')),
  );

  readonly error$ = this.selectError();

  readonly isDefaultBranchActive$: Observable<boolean> = combineLatest([this.activeBranch$, this.defaultBranch$]).pipe(
    map(([activeBranch, defaultBranch]) => activeBranch.name === defaultBranch?.name),
  );

  readonly currentOriginCommitSha$ = this.select((st) => st.ui.currentOriginCommitSha);
  readonly currentLocalCommitSha$ = this.select((st) => st.ui.currentLocalCommitSha);

  listen<Type extends GitEventType>(event: Type) {
    return this.event$.pipe(
      filter((evt) => event === evt.type),
      map((evt) => evt.params as GitEventTypeToGitEvent[Type]['params']),
    );
  }

  isInStatus(status: GitFSStatus['status']) {
    return this.gitStatusCode$.pipe(map((statusCode) => statusCode === status));
  }

  wasInStatus(status: GitFSStatus['status']) {
    return this.isInStatus(status).pipe(
      startWith(false),
      pairwise(),
      map(([was, is]) => was && !is),
      filter(Boolean),
    );
  }

  isOperationOngoing(options?: { exclude: Array<GitFSStatus['status']> } | { include: Array<GitFSStatus['status']> }) {
    const exclude = (options as { exclude: Array<GitFSStatus['status']> })?.exclude;
    const include = (options as { include: Array<GitFSStatus['status']> })?.include;

    const filterStatusCode = include
      ? filter<GitFSStatus['status'] | null>((status) => (status ? include.includes(status) : true))
      : exclude
      ? filter<GitFSStatus['status'] | null>((status) => (status ? !exclude.includes(status) : true))
      : filter(() => true);

    return this.gitStatusCode$.pipe(
      filterStatusCode,
      map((statusCode) => !!statusCode),
      distinctUntilChanged(),
    );
  }

  getActiveBranch() {
    return this.getActive() as GitBranch;
  }

  getActiveBranchName(): string | undefined {
    return this.getActiveBranch()?.name;
  }

  getDefaultBranchName() {
    return this.getValue().default as string;
  }

  getDefaultBranch() {
    const entities = this.getValue().entities;

    return entities && entities[this.getDefaultBranchName()];
  }

  getTreeItemAtPath(path: string, tree = this.getActiveBranch()?.tree): GitTreeItem | undefined {
    const pathItems = path.startsWith('/') ? path.substring(1).split('/') : path.split('/');

    let foundItem: GitTreeItem | undefined = pathItems[0] && tree ? tree[pathItems[0]] : undefined;

    for (const pathItem of pathItems.slice(1)) {
      foundItem = foundItem?.children?.[pathItem];
    }

    return foundItem;
  }

  getStatus() {
    return this.getValue().ui.status?.status || null;
  }

  getActiveTreeItem() {
    return this.getValue().ui.activeTreeItem;
  }

  getRepo() {
    return this.getValue().ui.repo;
  }

  getCommitsBehind(): number {
    return this.getValue().ui.behind || 0;
  }

  getCommitsAhead(): number {
    return this.getValue().ui.ahead || 0;
  }

  hasConflicts(): boolean {
    return this.getValue().ui.numberOfConflictedFiles > 0;
  }

  getModifiedFiles() {
    const fileStatuses = this.getValue().ui.fileStatuses;
    return Object.entries(fileStatuses).reduce((modified, [file, statusRow]) => {
      const [HEAD, WORKDIR] = statusRow;
      if (HEAD !== WORKDIR) {
        modified[file] = true;
      }

      return modified;
    }, {} as Record<string, true>);
  }

  getModifiedFilesList(): string[] {
    return Object.keys(this.getModifiedFiles());
  }

  getUntrackedFiles() {
    const fileStatuses = this.getValue().ui.fileStatuses;
    return Object.entries(fileStatuses).reduce((untracked, [file, statusRow]) => {
      const [HEAD, WORKDIR, STAGE] = statusRow;
      if (HEAD === 0 && WORKDIR === 2 && STAGE === 0) {
        untracked[file] = true;
      }

      return untracked;
    }, {} as Record<string, true>);
  }

  hasUncommittedChanges(changes: GitChangesConfig[]): boolean {
    const changedFiles = this.getModifiedFilesList();
    return changes.some(({ path, directChanges, prefix }) => {
      if (!prefix && !directChanges) {
        return changedFiles.includes(path);
      }

      if (prefix) {
        return changedFiles.some((change) => change.startsWith(path));
      }

      return changedFiles.some((change) => {
        const startsWith = change.startsWith(path);

        if (!startsWith) {
          return false;
        }

        return (
          change
            .replace(path, '')
            .split('/')
            .filter((chunk) => !!chunk).length === 1
        );
      });
    });
  }

  isDir(path: string) {
    const paths = path.split('/').filter((segment) => segment !== '.');
    const fileTree = this.getActiveBranch().tree;
    let currentNode = fileTree;
    let isDir = false;
    paths.forEach((segment, index) => {
      const last = index === paths.length - 1;
      if (!last && currentNode[segment]) {
        currentNode = currentNode[segment]?.children || currentNode;
      }

      if (last) {
        isDir = currentNode[segment]?.type === 'dir';
      }
    });

    return isDir;
  }

  /**
   * Returns `true` if any element exists under the given path.
   * @param path file or folder path
   */
  elementAtPathExists(path: string): boolean {
    const paths = path.split('/').filter((segment) => segment !== '.');
    const fileTree = this.getActiveBranch()?.tree;

    if (!fileTree) {
      return false;
    }

    let currentNode = fileTree;
    let folderExists = false;
    paths.forEach((currentPath, index) => {
      const last = index === paths.length - 1;
      if (!last && currentNode[currentPath]) {
        currentNode = currentNode[currentPath]?.children || currentNode;
      }

      if (last) {
        folderExists = !!currentNode[currentPath];
      }
    });

    return folderExists;
  }
}
